import type { BundleMetadata } from '@shared/support-bundles/contract'

/** The subset of `GET /api/about` a bundle needs. */
export interface AboutFields {
  version?: string | undefined
  buildDate?: string | undefined
  environment?: string | undefined
  region?: string | undefined
  ecsTaskId?: string | undefined
}

export interface BrowserFields {
  browser: string
  os: string
  screenResolution: string
  language: string
  timezone: string
}

export interface MetadataInput {
  about: AboutFields | undefined
  user: { id?: string; tenantId?: string; scopes?: string[] } | null
  browser: BrowserFields
  route: string
  featureFlags: Record<string, boolean>
}

const UNKNOWN = 'unknown'

/**
 * Assembles the environment block of a bundle.
 *
 * Deliberately total: `/api/about` can fail, and a bundle sent while the
 * backend is unhappy is exactly the bundle worth having — so a missing field
 * degrades to "unknown" rather than aborting the capture.
 *
 * The resolved feature flags are here because a mismatch between the flags a
 * customer actually has and the ones support assumes they have is a bug class
 * that is currently invisible from the outside.
 */
export function buildMetadata(input: MetadataInput): BundleMetadata {
  const { about, user, browser, route, featureFlags } = input

  return {
    app_version: about?.version ?? UNKNOWN,
    ...(about?.buildDate !== undefined && { build_date: about.buildDate }),
    environment: about?.environment ?? UNKNOWN,
    region: about?.region ?? UNKNOWN,
    ...(about?.ecsTaskId !== undefined && { ecs_task_id: about.ecsTaskId }),
    tenant_id: user?.tenantId ?? '',
    user_id: user?.id ?? '',
    scopes: user?.scopes ?? [],
    browser: browser.browser,
    os: browser.os,
    screen: browser.screenResolution,
    locale: browser.language,
    timezone: browser.timezone,
    route,
    feature_flags: featureFlags,
  }
}
